import React, { useState, useEffect } from 'react'
import {
  ArrowRight,
  ArrowUpRight,
  Building2,
  CheckCircle2,
  ChevronRight,
  Headset,
  Network,
  ShieldCheck,
  TrendingUp,
  Truck,
  Wallet,
  GraduationCap,
  RefreshCw,
  BriefcaseBusiness,
  Phone,
  Mail
} from 'lucide-react'
import FranchiseHeroImage from '../assets/images/data/franchise-hero.png'
import { Helmet } from 'react-helmet-async'
import Swal from 'sweetalert2'

const API_URL = process.env.REACT_APP_BACKEND_URL

const benefits = [
  {
    icon: Wallet,
    title: 'Low Investment, High Potential',
    description:
      'Start your logistics business with a practical investment and unlock exciting opportunities for long-term growth.'
  },
  {
    icon: GraduationCap,
    title: 'Comprehensive Training',
    description:
      'Get structured onboarding, professional training and operational guidance to run your franchise confidently.'
  },
  {
    icon: TrendingUp,
    title: 'Marketing & Branding Support',
    description:
      'Grow with our established brand identity, marketing resources and business development support.'
  },
  {
    icon: ShieldCheck,
    title: 'Advanced Technology',
    description:
      'Access modern logistics technology, shipment management tools and real-time operational dashboards.'
  },
  {
    icon: Headset,
    title: 'End-to-End Support',
    description:
      'Receive dedicated assistance across operations, customer service and everyday business activities.'
  },
  {
    icon: Network,
    title: 'Nationwide Network',
    description:
      'Connect with our expanding logistics network and serve customers across 18,000+ pin codes.'
  }
]

const initialFormData = {
  name: '',
  company: '',
  business_type: '',
  experience: '',
  investment: '',
  preferred_city: '',
  address: '',
  city: '',
  state: '',
  pincode: '',
  contact: '',
  email: '',
  message: '',
  captchaInput: ''
}

const inputClass =
  'w-full h-[50px] rounded-xl border border-gray-200 bg-white px-4 text-sm text-[#211f57] placeholder:text-gray-400 outline-none transition-all duration-200 focus:border-[#211f57] focus:ring-4 focus:ring-[#211f57]/10'

const labelClass = 'mb-2 block text-[13px] font-bold text-[#252447]'

const FranchiseRegistration = () => {
  const [formData, setFormData] = useState(initialFormData)
  const [captcha, setCaptcha] = useState({ question: '', answer: 0 })
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    generateCaptcha()
  }, [])

  const generateCaptcha = () => {
    const a = Math.floor(Math.random() * 10) + 1
    const b = Math.floor(Math.random() * 10) + 1

    setCaptcha({
      question: `${a} + ${b}`,
      answer: a + b
    })
  }

  const handleChange = e => {
    const { name, value } = e.target

    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const resetForm = () => {
    setFormData({ ...initialFormData })
    generateCaptcha()
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (Number(formData.captchaInput) !== captcha.answer) {
      Swal.fire({
        icon: 'error',
        title: 'Incorrect CAPTCHA',
        text: 'Please enter the correct answer and try again.',
        confirmButtonColor: '#d32c30'
      })

      setFormData(prev => ({
        ...prev,
        captchaInput: ''
      }))

      generateCaptcha()
      return
    }

    try {
      setIsSubmitting(true)

      const response = await fetch(`${API_URL}franchise/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(
          result.error || result.message || 'Failed to submit form.'
        )
      }

      await Swal.fire({
        icon: 'success',
        title: 'Registration Successful!',
        text: 'Thank you for your interest. Our franchise team will connect with you soon.',
        confirmButtonColor: '#211f57'
      })

      resetForm()
    } catch (error) {
      console.error('Franchise registration error:', error)

      Swal.fire({
        icon: 'error',
        title: 'Submission Failed',
        text: error.message || 'Please try again later.',
        confirmButtonColor: '#d32c30'
      })

      generateCaptcha()
    } finally {
      setIsSubmitting(false)
    }
  }

  const scrollToForm = () => {
    document.getElementById('franchise-form')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
  }

  const renderField = (id, label, type = 'text', required = true) => (
    <div key={id}>
      <label htmlFor={id} className={labelClass}>
        {label}
        {required && <span className='text-[#d32c30]'> *</span>}
      </label>

      <input
        id={id}
        name={id}
        type={type}
        value={formData[id]}
        onChange={handleChange}
        placeholder={`Enter ${label.replace('*', '').toLowerCase()}`}
        required={required}
        className={inputClass}
      />
    </div>
  )

  return (
    <>
      <Helmet>
        <title>Become a Franchise Partner | S Infinite Express</title>

        <meta
          name='description'
          content='Start your logistics franchise journey with S Infinite Express. Explore franchise opportunities, nationwide support and technology-driven logistics solutions.'
        />

        <meta
          name='keywords'
          content='Franchise, Logistics Franchise, Courier Franchise, S Infinite Express Franchise, Transport Franchise India, Franchise Opportunities, Delivery Business Franchise, Supply Chain Franchise, Franchise India, PTL FTL Franchise'
        />

        <meta name='robots' content='index, follow' />

        <link
          rel='canonical'
          href='https://sinfiniteexpress.com/join-us/franchise'
        />

        <meta property='og:type' content='website' />
        <meta
          property='og:title'
          content='Become a Franchise Partner | S Infinite Express'
        />
        <meta
          property='og:description'
          content="Join S Infinite Express's growing logistics franchise network with business support, technology and nationwide reach."
        />
        <meta
          property='og:url'
          content='https://sinfiniteexpress.com/join-us/franchise'
        />
        <meta
          property='og:image'
          content='https://sinfiniteexpress.com/assets/logo.png'
        />
      </Helmet>

      <main className='overflow-hidden bg-white font-sans text-gray-800'>
        {/* HERO SECTION */}
        <section className='relative overflow-hidden bg-[#211f57]'>
          <div className='absolute inset-0 pointer-events-none'>
            <div className='absolute -top-40 -right-32 h-[500px] w-[500px] rounded-full bg-[#d32c30]/20 blur-[120px]' />
            <div className='absolute -bottom-40 -left-32 h-[450px] w-[450px] rounded-full bg-[#fff44f]/10 blur-[120px]' />
          </div>

          <div className='relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 py-7 sm:px-4 md:py-10 lg:grid-cols-2 lg:gap-8 lg:px-5 lg:py-12'>
            {/* LEFT CONTENT */}
            <div className='relative z-10'>
              <h1 className='max-w-2xl text-4xl font-black leading-[1.13] tracking-tight text-white sm:text-5xl lg:text-[62px]'>
                Become A Part Of
                <span className='mt-2 block text-[#fff44f]'>
                  India's Growing
                </span>
                <span className='block'>Logistics Network</span>
              </h1>

              <p className='mt-7 max-w-xl text-base leading-8 text-white/75 sm:text-lg'>
                Build your own business with S Infinite Express. Explore
                exciting franchise opportunities backed by nationwide
                connectivity, advanced technology and dedicated operational
                support.
              </p>

              <div className='mt-9 flex flex-col gap-4 sm:flex-row'>
                <button
                  type='button'
                  onClick={scrollToForm}
                  className='group inline-flex items-center justify-center gap-3 rounded-xl bg-[#d32c30] px-7 py-4 text-sm font-extrabold text-white shadow-xl transition-all duration-300 hover:-translate-y-1 hover:bg-red-700'
                >
                  Apply For Franchise
                  <ArrowRight
                    size={18}
                    className='transition-transform group-hover:translate-x-1'
                  />
                </button>

                <a
                  href='tel:+918237323531'
                  className='inline-flex items-center justify-center gap-3 rounded-xl border border-white/25 bg-white/10 px-7 py-4 text-sm font-bold text-white transition hover:bg-white/20'
                >
                  <Phone size={18} />
                  +91 82373 23531
                </a>
              </div>

              {/* TRUST POINTS */}
              <div className='mt-12 grid grid-cols-2 gap-x-6 gap-y-5 border-t border-white/15 pt-8 sm:grid-cols-3'>
                <div>
                  <h3 className='text-2xl font-black text-[#fff44f]'>
                    18,000+
                  </h3>
                  <p className='mt-1 text-xs font-medium text-white/65'>
                    Pin Codes Network
                  </p>
                </div>

                <div>
                  <h3 className='text-2xl font-black text-[#fff44f]'>
                    Pan India
                  </h3>
                  <p className='mt-1 text-xs font-medium text-white/65'>
                    Network Reach
                  </p>
                </div>

                <div className='col-span-2 sm:col-span-1'>
                  <h3 className='text-2xl font-black text-[#fff44f]'>360°</h3>
                  <p className='mt-1 text-xs font-medium text-white/65'>
                    Business Support
                  </p>
                </div>
              </div>
            </div>

            {/* RIGHT IMAGE */}
            <div className='relative mx-auto w-full max-w-[570px] lg:ml-auto'>
              <div className='absolute -inset-5 rounded-[35px] bg-gradient-to-br from-[#fff44f]/20 to-[#d32c30]/20 blur-2xl' />

              <div className='relative overflow-hidden rounded-[28px] border border-white/20 bg-white/10 p-2 shadow-2xl'>
                <div className='relative overflow-hidden rounded-[22px]'>
                  <img
                    src={FranchiseHeroImage}
                    alt='S Infinite Express logistics franchise opportunity'
                    className='h-[340px] w-full object-cover sm:h-[430px] lg:h-[490px]'
                  />

                  <div className='absolute inset-0 bg-gradient-to-t from-[#101638]/90 via-transparent to-transparent' />

                  <div className='absolute bottom-0 left-0 right-0 p-5 sm:p-8'>
                    <h2 className='mt-4 max-w-md text-2xl font-black leading-tight text-white sm:text-3xl'>
                      Your Business.
                      <span className='block text-[#fff44f]'>Our Network.</span>
                    </h2>

                    <p className='mt-3 max-w-md text-sm leading-6 text-white/80'>
                      Together, let's move businesses forward with smarter
                      logistics solutions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* INTRO STRIP */}
        <section className='border-b border-gray-100 bg-white py-10'>
          <div className='mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-5 text-center sm:px-8 md:flex-row md:text-left lg:px-10'>
            <div>
              <p className='text-xs font-extrabold uppercase tracking-[2px] text-[#d32c30]'>
                Join Our Growing Network
              </p>
              <h2 className='mt-2 text-2xl font-black text-[#211f57] sm:text-3xl'>
                Let's Grow Your Business Together.
              </h2>
            </div>

            <button
              type='button'
              onClick={scrollToForm}
              className='group inline-flex shrink-0 items-center gap-3 rounded-xl bg-[#211f57] px-6 py-4 text-sm font-bold text-white transition hover:bg-[#d32c30]'
            >
              Explore Franchise
              <ArrowUpRight
                size={18}
                className='transition-transform group-hover:translate-x-1 group-hover:-translate-y-1'
              />
            </button>
          </div>
        </section>

        {/* WHY CHOOSE US */}
        <section className='bg-[#f8f9fc] px-5 py-10 sm:px-6 md:py-10 lg:px-8'>
          <div className='mx-auto max-w-7xl'>
            <div className='mx-auto mb-5 max-w-4xl text-center'>
              <span className='text-xs font-extrabold uppercase tracking-[2px] text-[#d32c30]'>
                Why Partner With Us
              </span>

              <h2 className='text-3xl font-black leading-tight text-[#211f57] sm:text-4xl'>
                Why Choose Our
                <span className='text-[#d32c30]'> Franchise Model?</span>
              </h2>

              <p className='mt-2 text-xs leading-7 text-gray-600 sm:text-base'>
                Build your future with S Infinite Express through powerful
                technology, nationwide reach and dedicated business support.
              </p>
            </div>

            <div className='grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3'>
              {benefits.map((item, index) => {
                const Icon = item.icon

                return (
                  <div
                    key={item.title}
                    className='group relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:border-[#fff44f] hover:shadow-xl'
                  >
                    <div className='absolute -right-10 -top-10 h-32 w-32 rounded-full bg-[#fff44f]/20 transition-transform duration-500 group-hover:scale-150' />

                    <div className='relative z-10'>
                      <div className='flex items-center justify-between'>
                        <div className='flex h-14 w-14 items-center justify-center rounded-2xl bg-[#211f57] text-[#ffff] transition-colors group-hover:bg-[#d32c30] group-hover:text-white'>
                          <Icon size={25} />
                        </div>

                        <span className='text-3xl font-black text-gray-100 group-hover:text-[#d32c30]'>
                          0{index + 1}
                        </span>
                      </div>

                      <h3 className='mt-7 text-lg font-extrabold text-[#211f57]'>
                        {item.title}
                      </h3>

                      <p className='mt-3 text-sm leading-7 text-gray-600'>
                        {item.description}
                      </p>

                      <div className='mt-6 flex items-center gap-2 text-xs font-extrabold uppercase tracking-wider text-[#d32c30]'>
                        Franchise Advantage
                        <ChevronRight size={15} />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* PROCESS SECTION */}
        <section className='bg-white px-5 py-8 sm:px-4 md:py-10 lg:px-5'>
          <div className='mx-auto max-w-7xl'>
            <div className='grid grid-cols-1 items-center gap-12 lg:grid-cols-[.8fr_1.2fr]'>
              <div>
                <span className='text-xs font-extrabold uppercase tracking-[2px] text-[#d32c30]'>
                  Simple & Transparent
                </span>

                <h2 className='mt-4 text-3xl font-black leading-tight text-[#211f57] sm:text-4xl'>
                  Start Your
                  <span className='block text-[#d32c30]'>
                    Franchise Journey
                  </span>
                </h2>

                <p className='mt-5 text-sm leading-7 text-gray-600'>
                  Take the first step towards becoming a franchise partner.
                  Share your details and our team will connect with you to
                  understand your business goals.
                </p>

                <button
                  type='button'
                  onClick={scrollToForm}
                  className='mt-8 inline-flex items-center gap-3 rounded-xl bg-[#d32c30] px-6 py-4 text-sm font-bold text-white transition hover:bg-[#211f57]'
                >
                  Register Your Interest
                  <ArrowRight size={18} />
                </button>
              </div>

              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                {[
                  {
                    number: '01',
                    title: 'Submit Your Details',
                    description:
                      'Complete the franchise registration form with your business information.',
                    icon: BriefcaseBusiness
                  },
                  {
                    number: '02',
                    title: 'Connect With Our Team',
                    description:
                      'Our team will review your details and discuss your preferred location.',
                    icon: Headset
                  },
                  {
                    number: '03',
                    title: 'Explore Opportunities',
                    description:
                      'Understand the business model, requirements and available opportunities.',
                    icon: Building2
                  },
                  {
                    number: '04',
                    title: 'Move Forward Together',
                    description:
                      'Take the next steps towards building your logistics business.',
                    icon: Truck
                  }
                ].map(step => {
                  const Icon = step.icon

                  return (
                    <div
                      key={step.number}
                      className='rounded-2xl border border-gray-100 bg-[#f8f9fc] p-6 transition hover:border-[#fff44f] hover:shadow-lg'
                    >
                      <div className='flex items-center justify-between'>
                        <div className='flex h-12 w-12 items-center justify-center rounded-xl bg-[#211f57] text-[#ffff]'>
                          <Icon size={22} />
                        </div>

                        <span className='text-3xl font-black text-gray-200 group-hover:text-[#d32c30]'>
                          {step.number}
                        </span>
                      </div>

                      <h3 className='mt-6 text-base font-extrabold text-[#211f57]'>
                        {step.title}
                      </h3>

                      <p className='mt-3 text-sm leading-6 text-gray-600'>
                        {step.description}
                      </p>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        {/* REGISTRATION FORM */}
        <section
          id='franchise-form'
          className='relative scroll-mt-20 overflow-hidden bg-[#f5f6fa] px-2 py-8 sm:px-4 md:py-12 lg:px-5'
        >
          <div className='absolute -left-32 top-0 h-80 w-80 rounded-full bg-[#fff44f]/20 blur-[100px]' />
          <div className='absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-[#d32c30]/10 blur-[100px]' />

          <div className='relative mx-auto max-w-6xl'>
            <div className='mb-5 text-center'>
              <span className='text-xs font-extrabold uppercase tracking-[2px] text-[#d32c30]'>
                Take The First Step
              </span>

              <h2 className='text-3xl font-black text-[#211f57] sm:text-4xl'>
                Franchise Registration
              </h2>

              <p className='mx-auto mt-2 max-w-4xl text-sm leading-7 text-gray-600 sm:text-base'>
                Interested in becoming a franchise partner? Fill out the form
                below. Our team will get in touch to discuss the opportunity.
              </p>
            </div>

            <div className='overflow-hidden rounded-[25px] border border-gray-100 bg-white shadow-xl'>
              {/* FORM HEADER */}
              <div className='relative overflow-hidden bg-[#211f57] px-3 py-4 sm:px-5'>
                <div className='absolute -right-10 -top-20 h-48 w-48 rounded-full border-[25px] border-white/5' />

                <div className='relative flex flex-col justify-between gap-5 sm:flex-row sm:items-center'>
                  <div>
                    <div className='mb-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#fff44f]'>
                      <CheckCircle2 size={15} />
                      Partner Registration
                    </div>

                    <h3 className='text-2xl font-black text-white'>
                      Let's Build Something Great.
                    </h3>

                    <p className='mt-2 text-sm text-white/65'>
                      Tell us about yourself and your business plans.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className='p-5 sm:p-8 lg:p-10'>
                {/* PERSONAL DETAILS */}
                <div className='mb-4'>
                  <div className='mb-3 flex items-center gap-3'>
                    <div>
                      <h4 className='font-extrabold text-[#211f57]'>
                        Personal & Business Information
                      </h4>
                      <p className='text-xs text-gray-500'>
                        Basic details about you and your company
                      </p>
                    </div>
                  </div>

                  <div className='grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3'>
                    {renderField('name', 'Full Name')}
                    {renderField('company', 'Company Name')}
                    {renderField('contact', 'Contact Number', 'tel')}
                    {renderField('email', 'Email Address', 'email')}
                    {renderField('address', 'Address')}
                    {renderField('city', 'City')}
                    {renderField('state', 'State')}
                    {renderField('pincode', 'Pincode')}

                    <div>
                      <label htmlFor='business_type' className={labelClass}>
                        Business Type <span className='text-[#d32c30]'>*</span>
                      </label>

                      <select
                        id='business_type'
                        name='business_type'
                        value={formData.business_type}
                        onChange={handleChange}
                        required
                        className={inputClass}
                      >
                        <option value=''>Select Business Type</option>
                        <option value='Sole Proprietor'>Sole Proprietor</option>
                        <option value='Partnership'>Partnership</option>
                        <option value='Pvt Ltd'>Pvt Ltd</option>
                        <option value='LLP'>LLP</option>
                        <option value='Other'>Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className='my-4 border-t border-gray-100' />

                {/* FRANCHISE DETAILS */}
                <div className='mb-4'>
                  <div className='mb-3 flex items-center gap-3'>
                    <div>
                      <h4 className='font-extrabold text-[#211f57]'>
                        Franchise Requirements
                      </h4>
                      <p className='text-xs text-gray-500'>
                        Share your experience and investment preferences
                      </p>
                    </div>
                  </div>

                  <div className='grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3'>
                    {renderField(
                      'experience',
                      'Years of Business Experience',
                      'number'
                    )}

                    {renderField('investment', 'Investment Budget (₹)', 'text')}

                    {renderField(
                      'preferred_city',
                      'Preferred Franchise Location',
                      'text'
                    )}
                  </div>
                </div>

                <div className='my-4 border-t border-gray-100' />

                {/* ADDITIONAL COMMENTS */}
                <div>
                  <label htmlFor='message' className={labelClass}>
                    Additional Comments
                  </label>

                  <textarea
                    id='message'
                    name='message'
                    rows='4'
                    value={formData.message}
                    onChange={handleChange}
                    placeholder='Tell us more about your business plans or any questions you may have...'
                    className='w-full resize-y rounded-xl border border-gray-200 bg-white px-4 py-4 text-sm text-[#211f57] outline-none transition-all placeholder:text-gray-400 focus:border-[#211f57] focus:ring-4 focus:ring-[#211f57]/10'
                  />
                </div>

                {/* CAPTCHA */}
                <div className='mt-8 rounded-2xl border border-[#211f57]/10 bg-[#f8f9fc] p-5 sm:p-6'>
                  <div className='flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between'>
                    <div>
                      <div className='flex items-center gap-2'>
                        <ShieldCheck size={19} className='text-[#211f57]' />
                        <h4 className='text-sm font-extrabold text-[#211f57]'>
                          Security Verification
                        </h4>
                      </div>

                      <p className='mt-2 text-xs text-gray-500'>
                        Solve the simple math question to verify your
                        submission.
                      </p>
                    </div>

                    <div className='flex items-center gap-3'>
                      <div className='flex h-[50px] min-w-[105px] items-center justify-center rounded-xl bg-[#211f57] px-5 text-lg font-black tracking-wider text-[#fff44f]'>
                        {captcha.question} = ?
                      </div>

                      <button
                        type='button'
                        onClick={generateCaptcha}
                        title='Refresh CAPTCHA'
                        className='flex h-[50px] w-[50px] shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-[#211f57] transition hover:bg-[#fff44f]'
                      >
                        <RefreshCw size={17} />
                      </button>
                    </div>
                  </div>

                  <div className='mt-5 max-w-sm'>
                    <label htmlFor='captchaInput' className={labelClass}>
                      Your Answer <span className='text-[#d32c30]'>*</span>
                    </label>

                    <input
                      id='captchaInput'
                      name='captchaInput'
                      type='number'
                      value={formData.captchaInput}
                      onChange={handleChange}
                      required
                      placeholder='Enter CAPTCHA answer'
                      className={inputClass}
                    />
                  </div>
                </div>

                {/* SUBMIT */}
                <div className='mt-9 flex flex-col items-center justify-between gap-5 border-t border-gray-100 pt-8 sm:flex-row'>
                  <p className='max-w-sm text-xs leading-6 text-gray-500'>
                    By submitting this form, you express your interest in
                    exploring a franchise opportunity with S Infinite Express.
                  </p>

                  <button
                    type='submit'
                    disabled={isSubmitting}
                    className='group inline-flex w-full items-center justify-center gap-3 rounded-xl bg-[#d32c30] px-10 py-4 text-sm font-extrabold text-white shadow-lg shadow-red-900/10 transition-all duration-300 hover:-translate-y-1 hover:bg-[#211f57] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto'
                  >
                    {isSubmitting ? (
                      <>
                        <RefreshCw size={18} className='animate-spin' />
                        SUBMITTING...
                      </>
                    ) : (
                      <>
                        SUBMIT REGISTRATION
                        <ArrowRight
                          size={18}
                          className='transition-transform group-hover:translate-x-1'
                        />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>

        {/* BOTTOM CTA */}
        <section className='relative overflow-hidden bg-[#211f57] px-5 py-16 sm:px-8 md:py-20 lg:px-10'>
          <div className='absolute -right-20 -top-32 h-80 w-80 rounded-full border-[45px] border-white/5' />
          <div className='absolute -bottom-32 -left-20 h-80 w-80 rounded-full bg-[#d32c30]/15 blur-3xl' />

          <div className='relative mx-auto flex max-w-7xl flex-col items-center justify-between gap-8 text-center md:flex-row md:text-left'>
            <div>
              <span className='text-xs font-extrabold uppercase tracking-[2px] text-[#fff44f]'>
                Your Journey Starts Here
              </span>

              <h2 className='mt-4 text-3xl font-black text-white sm:text-4xl'>
                Ready to Grow With Us?
              </h2>

              <p className='mt-4 max-w-2xl text-sm leading-7 text-white/70'>
                Take the first step towards becoming a part of the S Infinite
                Express franchise network.
              </p>
            </div>

            <div className='flex shrink-0 flex-col gap-3 sm:flex-row'>
              <button
                type='button'
                onClick={scrollToForm}
                className='inline-flex items-center justify-center gap-3 rounded-xl bg-[#fff44f] px-7 py-4 text-sm font-extrabold text-[#211f57] transition hover:bg-white'
              >
                Apply Now
                <ArrowRight size={18} />
              </button>

              <a
                href='mailto:support@sinfiniteexpress.com'
                className='inline-flex items-center justify-center gap-3 rounded-xl border border-white/25 px-7 py-4 text-sm font-bold text-white transition hover:bg-white/10'
              >
                <Mail size={17} />
                Contact Us
              </a>
            </div>
          </div>
        </section>
      </main>
    </>
  )
}

export default FranchiseRegistration
